import type { SidebarIconName } from "./icons";
import { ROUTES } from "../../routes";
import {
  AUDIT_READ_PERMISSION,
  AWARDS_READ_PERMISSION,
  DATA_PRODUCTS_READ_PERMISSION,
  POWERLIST_READ_PERMISSION,
} from "../../constants/permissions.js";

/**
 * A single navigable entry. `path` is both the route to navigate to and the
 * identity used to decide which entry is currently active.
 */
export type NavLeaf = {
  path: string;
  label: string;
  /** Rendered as a count pill. Omitted or 0 renders nothing. */
  badge?: number;
};

/** A labelled run of leaves inside a section, e.g. "Campaigns". */
export type NavGroup = {
  groupTitle: string;
  items: NavLeaf[];
};

export type NavChild = NavLeaf | NavGroup;

export function isNavGroup(child: NavChild): child is NavGroup {
  return "groupTitle" in child;
}

/**
 * A top-level sidebar entry. Either a link (`path`, no children) or an
 * expandable section (`children`, no path).
 */
export type NavSection = {
  id: string;
  label: string;
  /** Icon shown in the rail; the only thing visible when collapsed. */
  icon: SidebarIconName;
  path?: string;
  children?: NavChild[];
};

/**
 * A non-clickable heading grouping related sections. A zone with no visible
 * sections is dropped entirely so no orphan heading is rendered.
 */
export type NavZone = {
  id: string;
  /** Omitted for the leading ungrouped run (Home). */
  label?: string;
  sections: NavSection[];
};

export type NavAccess = {
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasFlag: (flag: string) => boolean;
  isProfilesEnabled: boolean;
  isLoadTestAccountsActive?: boolean;
  unhandledCrmCount: number;
};

/** Drops falsy entries so gates can be written inline as `cond && entry`. */
function compact<T>(entries: (T | false | null | undefined)[]): T[] {
  return entries.filter((entry): entry is T => Boolean(entry));
}

/** Builds a group, or nothing at all when every item inside it is gated out. */
function group(groupTitle: string, items: (NavLeaf | false | null | undefined)[]): NavGroup | null {
  const visible = compact(items);
  return visible.length > 0 ? { groupTitle, items: visible } : null;
}

/** Builds a section, or nothing at all when every child inside it is gated out. */
function section(
  base: Omit<NavSection, "children">,
  children: (NavChild | false | null | undefined)[],
): NavSection | null {
  const visible = compact(children);
  return visible.length > 0 ? { ...base, children: visible } : null;
}

/** Builds a zone, dropping it entirely when it would render no sections. */
function zone(id: string, label: string | undefined, sections: (NavSection | null)[]): NavZone[] {
  const visible = compact(sections);
  return visible.length > 0 ? [{ id, label, sections: visible }] : [];
}

/**
 * The whole staff-ui navigation, as five zones of sections.
 *
 * Permission gating lives here rather than in the component so the shell stays
 * readable. Every gate is the same one the flat nav used before the regrouping —
 * where a section used to wrap a child in an implicit gate (the old
 * `Legal500.com Data` and `Developer` sections both did), that gate is written
 * out explicitly on the child so the effective visibility is unchanged.
 */
export function buildNavZones(access: NavAccess): NavZone[] {
  const {
    hasPermission,
    hasAnyPermission,
    hasFlag,
    isProfilesEnabled,
    isLoadTestAccountsActive = false,
    unhandledCrmCount,
  } = access;

  const canSeeTaxonomy = hasPermission("taxonomy.read");
  const canSeeFulfilment = hasAnyPermission([
    "fulfilment.read_subscriptions",
    "fulfilment.read_packages",
    "fulfilment.read_features",
  ]);
  const canSeeFulfilmentFeatures = hasPermission("fulfilment.read_features");
  const canSeeFulfilmentPackages = hasPermission("fulfilment.read_packages");
  const canSeeFulfilmentSubscriptions = hasPermission("fulfilment.read_subscriptions");
  const canSeeFulfilmentCrm = hasPermission("fulfilment.read_subscriptions");
  const canSeePlatformUsers = hasPermission("user.read_accounts");
  // Both nav entries lead to pages that gate on the read grant, so the write grant alone opens
  // nothing: offering it would lead an operator to an Access Denied page.
  const canSeeWebsiteCompanies = hasPermission("staff.website_companies.read");
  const canSeeWebsiteUsers = hasPermission("staff.website_users.read");
  const canSeeRegistrationGate = hasPermission("staff.registration_gate.read");
  const canSeeAddressBook = hasPermission("addressbook.read");
  const canSeeDataMarts = hasPermission("data-marts.read");
  const canSeeAwards = hasPermission(AWARDS_READ_PERMISSION);
  const canSeeDataProducts = hasPermission(DATA_PRODUCTS_READ_PERMISSION);
  const canSeePowerlist = hasPermission(POWERLIST_READ_PERMISSION);
  const canSeeDocumentParsing = hasAnyPermission([
    "submission.parsing.jobs",
    "submission.parsing.sharepoint",
  ]);
  // The AI submission parser sits behind one permission: its schemas are what the worker spends
  // money against and its markdown templates execute EJS server-side, so configuring it and
  // reading its stats are not separately gated. See docs/ai-submission-parser/DESIGN.md.
  //
  // The flag here is the visibility one, deliberately not the worker's. Showing the screens costs
  // nothing and is how a schema gets authored and reviewed before the first run is paid for;
  // stopping the pipeline is submissions-ai-parser-worker (and automatic parsing also needs submissions-ai-parser-auto-parse), and a menu must never be what turns
  // that off.
  const canSeeAiParser = hasFlag("submissions-ai-parser") && hasPermission("submission.config");
  const canSeeSubmissionsTab = hasAnyPermission([
    "staff.submission.read",
    "staff.referee.read",
    "submission.parsing.jobs",
    "submission.parsing.sharepoint",
    // A schema author who holds only submission.config (no read/parsing permission) still needs
    // the section open to reach AI Schemas/Templates/Runs below.
    "submission.config",
  ]);
  const canSeeSubmissionsList = hasPermission("staff.submission.read");
  const canSeeReferees = hasPermission("staff.referee.read");
  const canSeeResearchTab = hasAnyPermission([
    "staff.approval.read",
    "staff.research.read",
    "staff.research.create",
  ]);
  const canSeeCommentaries = hasAnyPermission([
    "staff.research.read",
    "staff.research.create",
    "staff.research.write",
  ]);
  const canSeePlatform = hasAnyPermission(["platform.read"]);
  const canSeeAudit = hasPermission("audit.read");
  const canSeeL500Data = hasAnyPermission(["l500_data.read"]);
  const canSeeRedisLoading = hasAnyPermission(["l500_data.redis.read"]);
  const canSeeWpSyncer = hasAnyPermission(["l500_data.wp_syncer.read"]);
  const canSeeIntelligenceSyncer = hasAnyPermission(["l500_data.intelligence_syncer.read"]);
  // Read, not write: the page shows past uploads to anyone who can read the address
  // book, and only offers the upload itself to those who can write. Still flag-gated,
  // so it stays hidden until someone turns it on under Platform > Operations > Feature Flags.
  const canSeeBulkExternalRefs =
    hasFlag("bulk-external-references") && hasAnyPermission(["addressbook.read"]);
  const canSeeDeveloper = hasAnyPermission([
    "developer.job_runs",
    "developer.write",
    "swagger:read",
    "platform.read",
    "submission.parsing.jobs",
    "submission.parsing.sharepoint",
  ]);
  const canSeeCrmWebhookTool = hasPermission("developer.write");
  // One-off bulk migration tool: same permission as its former Developer neighbours, so
  // the link is only shown to people who can actually run it.
  const canSeeWebsiteRelabel = hasPermission("developer.write");
  // One-off remediation tool (DEV-2803): gated exactly like Website Relabel.
  const canSeeOfficeIdentityBackfill = hasPermission("developer.write");
  // One-off remediation tool (SOFTSUPP-333 follow-up): gated exactly like its
  // Office Identity Backfill neighbour.
  const canSeeOfficeSlugBackfill = hasPermission("developer.write");
  // One-off remediation tool: gated exactly like its Office Slug Backfill neighbour.
  const canSeeContactSlugBackfill = hasPermission("developer.write");
  // DEV-2662: gated on the endpoints' own permissions rather than developer.write — the
  // backfill needs staff.submission.write and its stats staff.submission.read, and
  // staff.developer holds neither, so the page would only 403 for it.
  const canSeeContactMatchBackfill =
    hasPermission("staff.submission.write") && hasPermission("staff.submission.read");
  // One-off remediation tool (SOFTSUPP-506): gated exactly like its
  // Office Identity Backfill neighbour.
  const canSeeContactRoleEndDateBackfill = hasPermission("developer.write");
  // One-off remediation tool: gated exactly like its Contact Role End Date
  // Backfill neighbour.
  const canSeeSurveyTestimonialReplay = hasPermission("developer.write");
  const canSeeAddressBookRenameReplay =
    hasPermission("developer.write") && hasPermission("addressbook.read");
  const canSeeNameTranslationsImport = hasPermission("developer.write");
  // Bulk import that can wipe and rewrite the awards data: gated exactly like
  // the other maintenance tools that write.
  const canSeeAwardsImport = hasPermission("developer.write");
  const canSeeFirmLogoCopy = hasPermission("l500_data.wp_syncer.read");
  // Not behind canSeeDeveloper: the log shows the full text of every edit, so `audit.read`
  // (user admins, platform admins and super admins) is the whole gate.
  const canSeeAuditLog = hasPermission(AUDIT_READ_PERMISSION);
  const canSeeStatistics = hasPermission("staff.approval.read");
  const canSeeContentInbox = hasPermission("staff.approval.read");
  const canSeeResearchCms = hasPermission("staff.research.read");
  const canSeeSurveys = hasPermission("staff.survey.read");
  const canSeeCms = hasPermission("cms.read");
  // Dedicated cms.events.* domain, not the generic cms.* — a role made of cms.read/write
  // would hand Events Media every page and block as well (task 2.8).
  const canSeeEvents = hasPermission("cms.events.read");
  const canSeePageContent = hasPermission("cms.page_fields.read");
  const canSeeProfiles = hasAnyPermission(["profiles.read", "profiles.view_all"]);
  // Deliberately the dedicated bulk-move permission (staff.profiles_admin only),
  // NOT profiles.write — same gate as the move tool itself.
  const canSeeContentMoves = hasPermission("profiles.bulk_move");
  const canSeeRefereeManagement = hasAnyPermission(["staff.campaign.read", "staff.campaign.write"]);
  const canSeeMarketingToolkit = hasAnyPermission([
    "staff.marketing.read",
    "staff.marketing.write",
  ]);
  const canSeeFirmDiagnostics = hasPermission("staff.marketing.diagnostics");

  return [
    ...zone("home", undefined, [{ id: "home", label: "Home", icon: "home", path: "/" }]),

    ...zone("editorial", "Editorial", [
      section({ id: "submissions", label: "Submissions", icon: "submissions" }, [
        canSeeSubmissionsTab &&
          canSeeSubmissionsList && {
            path: ROUTES.submissions.list,
            label: "Submissions",
          },
        canSeeSubmissionsTab &&
          canSeeSubmissionsList && {
            path: ROUTES.submissions.rollingSubmissions,
            label: "Rolling Submissions",
          },
        canSeeSubmissionsTab &&
          canSeeReferees && {
            path: ROUTES.submissions.referees,
            label: "Referees",
          },
        canSeeSubmissionsTab &&
          canSeeDocumentParsing && {
            path: ROUTES.submissions.documentParsing,
            label: "Document Parsing",
          },
        // Moved out of the old Developer section. Both its old home and this one
        // open for `submission.parsing.*`, so the audience is unchanged.
        canSeeSubmissionsTab &&
          canSeeDocumentParsing && {
            path: ROUTES.developer.parseMonitoring,
            label: "Parse Monitoring",
          },
        canSeeSubmissionsTab &&
          canSeeAiParser && {
            path: ROUTES.submissions.aiSchemas,
            label: "AI Schemas",
          },
        canSeeSubmissionsTab &&
          canSeeAiParser && {
            path: ROUTES.submissions.aiMarkdownTemplates,
            label: "AI Markdown Templates",
          },
        canSeeSubmissionsTab &&
          canSeeAiParser && {
            path: ROUTES.submissions.aiParseRuns,
            label: "AI Parse Runs",
          },
      ]),

      section({ id: "research", label: "Research", icon: "research" }, [
        canSeeResearchTab &&
          canSeeCommentaries && {
            path: ROUTES.research.commentaries,
            label: "Research Assignment",
          },
        canSeeResearchTab &&
          canSeeContentInbox && {
            path: ROUTES.research.contentInbox,
            label: "Testimonials",
          },
        canSeeResearchTab &&
          canSeeContentInbox && {
            path: ROUTES.research.testimonials,
            label: "Approved Testimonials",
          },
        canSeeAuditLog && {
          path: ROUTES.research.auditLog,
          label: "Audit Log",
        },
        canSeeResearchTab &&
          canSeeResearchCms && {
            path: ROUTES.research.cms,
            label: "Research Portal",
          },
        canSeeResearchTab &&
          canSeeStatistics && {
            path: ROUTES.research.statistics,
            label: "Statistics",
          },
      ]),

      section({ id: "surveys", label: "Surveys", icon: "surveys" }, [
        canSeeSurveys && { path: ROUTES.surveys.management, label: "Surveys" },
        canSeeSurveys && { path: ROUTES.surveys.responses, label: "Responses" },
        canSeeSurveys && {
          path: ROUTES.surveys.questionLibrary,
          label: "Question Library",
        },
        canSeeSurveys && {
          path: ROUTES.surveys.randomQuestions,
          label: "Random Questions",
        },
        group("Campaigns", [
          canSeeRefereeManagement && {
            path: ROUTES.surveys.campaigns,
            label: "Survey Campaigns",
          },
          canSeeRefereeManagement && {
            path: ROUTES.surveys.templates,
            label: "Mailing Templates",
          },
          canSeeRefereeManagement && {
            path: ROUTES.surveys.optOuts,
            label: "Opt-out Management",
          },
        ]),
      ]),
    ]),

    ...zone("publishing", "Publishing", [
      section({ id: "profiles", label: "Profiles", icon: "profiles" }, [
        canSeeProfiles &&
          isProfilesEnabled && {
            path: ROUTES.profiles.dashboard,
            label: "Dashboard",
          },
        canSeeProfiles &&
          isProfilesEnabled && {
            path: ROUTES.profiles.firms,
            label: "Firm Inspector",
          },
        canSeeProfiles && isProfilesEnabled
          ? group("Content", [
              { path: ROUTES.profiles.firmProfiles, label: "Firm profiles" },
              {
                path: ROUTES.profiles.lawyerProfiles,
                label: "Lawyer profiles",
              },
              { path: ROUTES.profiles.firmTabs, label: "Firm tabs" },
              { path: ROUTES.profiles.teamPages, label: "Team pages" },
              { path: ROUTES.profiles.articles, label: "Articles" },
            ])
          : null,
        canSeeProfiles && isProfilesEnabled
          ? group("Admin", [
              canSeeContentMoves && {
                path: ROUTES.profiles.contentMoves,
                label: "Content Moves",
              },
              { path: ROUTES.profiles.audit, label: "Audit" },
              { path: ROUTES.profiles.userGuide, label: "User guide" },
            ])
          : null,
      ]),

      section({ id: "cms", label: "Website CMS", icon: "cms" }, [
        canSeeCms && { path: ROUTES.cms.homepage, label: "Homepage" },
        canSeeCms && { path: ROUTES.cms.pages, label: "Pages" },
        canSeeCms &&
          canSeePageContent && {
            path: ROUTES.cms.pageContent,
            label: "Page Content",
          },
        canSeeCms && { path: ROUTES.cms.menus, label: "Menus" },
      ]),

      section({ id: "events", label: "Events", icon: "events" }, [
        canSeeEvents && { path: ROUTES.events.list, label: "Events" },
        canSeeEvents && {
          path: ROUTES.events.heroBanner,
          label: "Hero Banner",
        },
      ]),

      section({ id: "marketing", label: "Marketing Toolkit", icon: "marketing" }, [
        canSeeMarketingToolkit && {
          path: ROUTES.marketingToolkit.copyTemplates,
          label: "Copy Templates",
        },
        canSeeMarketingToolkit && {
          path: ROUTES.marketingToolkit.embargoTexts,
          label: "Embargo Wording",
        },
        canSeeMarketingToolkit && {
          path: ROUTES.marketingToolkit.terms,
          label: "Terms & Conditions",
        },
        canSeeMarketingToolkit && {
          path: ROUTES.marketingToolkit.downloadAudit,
          label: "Download Audit",
        },
        canSeeMarketingToolkit && {
          path: ROUTES.marketingToolkit.userGuide,
          label: "User Guide",
        },
        canSeeFirmDiagnostics && {
          path: ROUTES.marketingToolkit.firmDiagnostics,
          label: "Firm Diagnostics",
        },
        canSeeFirmDiagnostics && {
          path: ROUTES.marketingToolkit.regionMapping,
          label: "Region Mapping",
        },
      ]),

      // Not in the Concept A prototype, which predates it. Publishing is the closest
      // fit: it is editorial output management, not reference data or platform plumbing.
      section({ id: "awards", label: "Awards", icon: "awards" }, [
        canSeeAwards && { path: ROUTES.awards.list, label: "Manage Awards" },
      ]),

      section({ id: "gc-powerlist", label: "GC Powerlist", icon: "powerlist" }, [
        canSeePowerlist && { path: ROUTES.powerlist.series, label: "Series & Editions" },
        canSeePowerlist && { path: ROUTES.powerlist.questions, label: "Question Bank" },
      ]),
    ]),

    ...zone("reference", "Reference data", [
      section({ id: "taxonomy", label: "Taxonomy", icon: "taxonomy" }, [
        canSeeTaxonomy && { path: ROUTES.taxonomy.regions, label: "Regions" },
        canSeeTaxonomy && {
          path: ROUTES.taxonomy.regionCountries,
          label: "WordPress Regions",
        },
        canSeeTaxonomy && {
          path: ROUTES.taxonomy.directories,
          label: "Directories",
        },
        canSeeTaxonomy && {
          path: ROUTES.taxonomy.bpas,
          label: "Business Practice Areas",
        },
        canSeeTaxonomy && { path: ROUTES.taxonomy.editions, label: "Editions" },
        canSeeTaxonomy && {
          path: ROUTES.taxonomy.industrySectors,
          label: "Industry Sectors",
        },
        canSeeTaxonomy && {
          path: ROUTES.taxonomy.miscellaneous,
          label: "Miscellaneous",
        },
      ]),

      section({ id: "address-book", label: "Address Book", icon: "addressBook" }, [
        canSeeAddressBook && {
          path: ROUTES.addressBook.companies,
          label: "Companies",
        },
        canSeeAddressBook && {
          path: ROUTES.addressBook.contacts,
          label: "Contacts",
        },
        canSeeAddressBook && {
          path: ROUTES.addressBook.offices,
          label: "Offices",
        },
        // Moved out of the old Legal500.com Data section. Its gate already required
        // `addressbook.read`, which is this section's gate, so nobody gains or loses it.
        canSeeBulkExternalRefs && {
          path: ROUTES.l500Data.bulkExternalRefs,
          label: "Bulk External Refs",
        },
      ]),

      ...(canSeeDataMarts
        ? [
            {
              id: "data-marts",
              label: "Data Marts",
              icon: "dataMarts",
              path: ROUTES.dataMarts.root,
            } satisfies NavSection,
          ]
        : []),
    ]),

    ...zone("commercial", "Commercial", [
      section({ id: "fulfilment", label: "Fulfilment", icon: "fulfilment" }, [
        canSeeFulfilment &&
          canSeeFulfilmentSubscriptions && {
            path: ROUTES.fulfilment.subscriptions,
            label: "Subscriptions",
          },
        canSeeFulfilment &&
          canSeeFulfilmentPackages && {
            path: ROUTES.fulfilment.packages,
            label: "Packages",
          },
        canSeeFulfilment &&
          canSeeFulfilmentFeatures && {
            path: ROUTES.fulfilment.features,
            label: "Features",
          },
        canSeeFulfilment
          ? group("CRM", [
              canSeeFulfilmentCrm && {
                path: ROUTES.fulfilment.crmPackageMappings,
                label: "CRM Package Mappings",
              },
              canSeeFulfilmentCrm && {
                path: ROUTES.fulfilment.crmWebhookLogs,
                label: "CRM Webhook",
                badge: unhandledCrmCount,
              },
            ])
          : null,
      ]),

      section({ id: "data-products", label: "Data products", icon: "dataMarts" }, [
        canSeeDataProducts && { path: ROUTES.dataProducts.omni, label: "Omni dashboards" },
        canSeeDataProducts && {
          path: ROUTES.dataProducts.historicalData,
          label: "Historical Data dashboards",
        },
      ]),

      section({ id: "people", label: "People & Access", icon: "people" }, [
        group("Website", [
          canSeeWebsiteUsers && { path: ROUTES.users.website, label: "Users" },
          canSeeWebsiteCompanies && {
            path: ROUTES.websiteUsers.companies,
            label: "Companies",
          },
          canSeeWebsiteCompanies && {
            path: ROUTES.websiteUsers.pendingCompanies,
            label: "Pending companies",
          },
          canSeeRegistrationGate && {
            path: ROUTES.registrationGate,
            label: "Email Gate",
          },
        ]),
        group("Account services", [
          canSeePlatformUsers && {
            path: ROUTES.users.accountServices,
            label: "Users",
          },
          canSeePlatformUsers && {
            path: ROUTES.users.organisations,
            label: "Organisations",
          },
          canSeePlatformUsers && {
            path: ROUTES.users.userVerification,
            label: "User Verification",
          },
          canSeePlatformUsers && {
            path: ROUTES.users.regionPublications,
            label: "Region Publications",
          },
        ]),
        group("Staff", [
          canSeePlatformUsers && {
            path: ROUTES.users.staff,
            label: "Staff users",
          },
          canSeePlatformUsers && {
            path: ROUTES.machineAccounts.list,
            label: "Machine Accounts",
          },
          canSeePlatformUsers && {
            path: ROUTES.users.roles,
            label: "Roles & Permissions",
          },
        ]),
      ]),
    ]),

    ...zone("platform", "Platform", [
      section({ id: "sync", label: "Sync & Pipelines", icon: "sync" }, [
        canSeeL500Data &&
          canSeeWpSyncer && {
            path: ROUTES.l500Data.wpSyncer,
            label: "WP Syncer",
          },
        canSeeL500Data &&
          canSeeWpSyncer && {
            path: ROUTES.l500Data.wpSyncerImageCache,
            label: "WP Image Cache",
          },
        canSeeL500Data &&
          canSeeIntelligenceSyncer && {
            path: ROUTES.l500Data.intelligenceSyncer,
            label: "Intelligence Syncer",
          },
        canSeeDeveloper && { path: ROUTES.developer.syncers, label: "Syncers" },
        canSeeDeveloper && {
          path: ROUTES.developer.queueMonitor,
          label: "Queue Monitor",
        },
        canSeeDeveloper && {
          path: ROUTES.developer.pipelineBrowser,
          label: "Pipeline Browser",
        },
        canSeeL500Data &&
          canSeeRedisLoading && {
            path: ROUTES.l500Data.redisLoading,
            label: "Redis Data Loading",
          },
        canSeeL500Data &&
          canSeeRedisLoading && {
            path: ROUTES.l500Data.redisBreakdown,
            label: "Redis Breakdown",
          },
        canSeeL500Data &&
          canSeeWpSyncer && {
            path: ROUTES.l500Data.promoteImpact,
            label: "Promote Impact",
          },
      ]),

      section({ id: "operations", label: "Operations", icon: "operations" }, [
        canSeeAudit && {
          path: ROUTES.audit.list,
          label: "Audit",
        },
        canSeeDeveloper &&
          canSeePlatform && {
            path: ROUTES.developer.services,
            label: "Services",
          },
        canSeeDeveloper &&
          canSeePlatform && {
            path: ROUTES.developer.featureFlags,
            label: "Feature Flags",
          },
        canSeePlatform &&
          isLoadTestAccountsActive && {
            path: ROUTES.developer.loadTestAccounts,
            label: "Load-test Accounts",
          },
        canSeeDeveloper &&
          canSeePlatform && {
            path: ROUTES.developer.notifications,
            label: "Notifications",
          },
        canSeeDeveloper &&
          canSeePlatform && {
            path: ROUTES.gatewayAllowlist.list,
            label: "Gateway Allowlist",
          },
        canSeeDeveloper && { path: ROUTES.developer.swagger, label: "Swagger" },
        // Renamed from the bare "Users" it carried under Developer, which would now
        // collide with People & Access > Account services > Users.
        canSeeDeveloper && {
          path: ROUTES.developer.users,
          label: "Developer Users",
        },
        canSeeDeveloper && {
          path: ROUTES.developer.researchDb,
          label: "Research DB Viewer",
        },
      ]),

      section(
        {
          id: "maintenance",
          label: "Maintenance Scripts",
          icon: "maintenance",
        },
        [
          canSeeDeveloper && {
            path: ROUTES.developer.accountRemediation,
            label: "Account Remediation",
          },
          canSeeDeveloper && {
            path: ROUTES.developer.companySlugDrift,
            label: "Company Slug Drift",
          },
          canSeeWebsiteRelabel && {
            path: ROUTES.developer.websiteRelabel,
            label: "Profile Website Change",
          },
          canSeeOfficeIdentityBackfill && {
            path: ROUTES.developer.officeIdentityBackfill,
            label: "Office Identity Backfill",
          },
          canSeeOfficeSlugBackfill && {
            path: ROUTES.developer.officeSlugBackfill,
            label: "Office Slug Backfill",
          },
          canSeeContactSlugBackfill && {
            path: ROUTES.developer.contactSlugBackfill,
            label: "Contact Slug Backfill",
          },
          canSeeContactMatchBackfill && {
            path: ROUTES.developer.contactMatchBackfill,
            label: "Contact Match Backfill",
          },
          canSeeContactRoleEndDateBackfill && {
            path: ROUTES.developer.contactRoleEndDateBackfill,
            label: "Contact Role End Dates",
          },
          canSeeSurveyTestimonialReplay && {
            path: ROUTES.developer.surveyTestimonialReplay,
            label: "Survey Testimonial Replay",
          },
          canSeeAddressBookRenameReplay && {
            path: ROUTES.developer.addressBookRenameReplay,
            label: "Address Book Rename Replay",
          },
          canSeeDeveloper && {
            path: ROUTES.developer.researchDataImport,
            label: "Research Data Import",
          },
          canSeeDeveloper &&
            canSeeDocumentParsing && {
              path: ROUTES.developer.editorialTransform,
              label: "Editorial Transform",
            },
          canSeeCrmWebhookTool && {
            path: ROUTES.developer.crmWebhook,
            label: "CRM Webhook Tester",
          },
          canSeeNameTranslationsImport && {
            path: ROUTES.developer.nameTranslationsImport,
            label: "Name Translations Import",
          },
          canSeeAwardsImport && {
            path: ROUTES.developer.awardsImport,
            label: "Awards Import",
          },
          canSeeFirmLogoCopy && {
            path: ROUTES.developer.firmLogoCopy,
            label: "Firm Logo Copy",
          },
          canSeeFulfilment &&
            canSeeFulfilmentSubscriptions && {
              path: ROUTES.fulfilment.bulkSubscriptionUpdate,
              label: "Subscription Audit",
            },
        ],
      ),
    ]),
  ];
}
